import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BuilderApi, OwnerCommand, Workspace } from "../api/contract";
import { toBuilderError } from "../api/errors";

export interface UseLibraryOptions {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace | undefined;
  /** Only read while the Content view is open. */
  enabled: boolean;
  refresh: () => Promise<unknown>;
}

export type LibraryChange = { recordId: string; status: "saving" | "failed" };

let sequence = 0;
const actionId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(sequence++).toString(36)}`.slice(0, 64);

/**
 * The owner's content library for the current draft, and the changes they make to it directly:
 * editing text, deleting something no page shows directly, and restoring it. Each change applies
 * straight away to the private draft; deleting can be undone from the toast.
 */
export function useLibrary({ api, siteId, workspace, enabled, refresh }: UseLibraryOptions) {
  const draftId = workspace?.site.draftId;
  const query = useQuery({
    queryKey: ["builder", "content", siteId, draftId],
    queryFn: ({ signal }) => api.getContent(siteId, signal),
    enabled: enabled && Boolean(draftId),
    staleTime: 0,
    retry: (count, caught) => toBuilderError(caught).outcomeUnknown && count < 2,
  });
  const [change, setChange] = useState<LibraryChange | null>(null);

  const run = useCallback(async (recordId: string, prefix: string, command: OwnerCommand): Promise<boolean> => {
    if (!draftId) return false;
    setChange({ recordId, status: "saving" });
    try {
      const action = await api.submitAction(siteId, { actionId: actionId(prefix), baseDraftId: draftId, command });
      if (action.state !== "succeeded" && action.state !== "queued" && action.state !== "running") throw new Error("Not applied");
      await Promise.all([refresh(), query.refetch()]);
      setChange(null);
      return true;
    } catch {
      setChange({ recordId, status: "failed" });
      return false;
    }
  }, [api, siteId, draftId, refresh, query]);

  return {
    library: query.data,
    loading: query.isLoading,
    failed: Boolean(query.error),
    retry: () => void query.refetch(),
    change,
    save: (recordId: string, set: Record<string, string | null>) => run(recordId, "edit", { kind: "content.patch", recordId, set }),
    remove: (recordId: string) => run(recordId, "delete", { kind: "content.delete", recordId }),
    restore: (recordId: string, fromDraftId: string) => run(recordId, "restore", { kind: "content.restore", recordId, fromDraftId }),
    /** Moves one record within its list; `memberIds` is the whole collection in its new order. */
    reorder: (recordId: string, collectionId: string, memberIds: string[]) => run(recordId, "reorder", { kind: "collection.reorder", collectionId, memberIds }),
  };
}

export type LibraryController = ReturnType<typeof useLibrary>;
